import { api } from "../AxiosInstance";
import {
  IUserFilters,
  ICreateUserData,
  IUpdateUserData,
  IUserActivity,
  IUserManagementStats,
  IAccountType,
} from "../interfaces/userManagement.interface";
import { IUser } from "../interfaces/user.interface";

const UserManagementService = {
  // Get users based on current user's permissions
  getUsers: async (
    filters?: IUserFilters,
    userProfile?: IUser
  ): Promise<IUser[]> => {
    try {
      // Determine endpoint based on user role
      let endpoint = "/users";

      if (userProfile?.role?.name === "ROLE_CLIENT_ADMIN") {
        endpoint = "/enterprise/users";
      } else if (userProfile?.role?.name === "ROLE_FEDERATION_ADMIN") {
        endpoint = "/fed/users";
      } else if (userProfile?.role?.name === "ROLE_TRANSPORTER_ADMIN") {
        endpoint = "/transporter/users";
      }

      const response = await api.get(endpoint, { params: filters });
      return response.data;
    } catch (error: any) {
      console.error("Error fetching users:", error);
      throw error;
    }
  },

  // Create user based on current user's permissions
  createUser: async (
    userData: ICreateUserData,
    userProfile?: IUser
  ): Promise<IUser> => {
    try {
      let endpoint = "/api/v2/users";

      // Route to appropriate endpoint based on current user's role
      if (userProfile?.role?.name === "ROLE_CLIENT_ADMIN") {
        if (userData.userType === "FEDERATION") {
          endpoint = "/api/v2/enterprise/registration/fed-admin";
        } else if (userData.userType === "TRANSPORTER") {
          endpoint = "/api/v2/enterprise/registration/transporter-admin";
        }
      } else if (userProfile?.role?.name === "ROLE_FEDERATION_ADMIN") {
        if (userData.userType === "TRANSPORTER") {
          endpoint = "/api/v2/fed/registration/transporter-admin";
        }
      }

      const response = await api.post(endpoint, {
        name: userData.name,
        username: userData.username,
        email: userData.email,
        password: userData.password,
        phoneNumber: userData.phoneNumber,
        identifier: userData.identifier,
      });
      return response.data;
    } catch (error: any) {
      console.error("Error creating user:", error);
      throw error;
    }
  },

  // Update user
  updateUser: async (
    userId: number,
    userData: IUpdateUserData
  ): Promise<IUser> => {
    try {
      const response = await api.put(`/users/${userId}`, userData);
      return response.data;
    } catch (error: any) {
      console.error("Error updating user:", error);
      throw error;
    }
  },

  // Delete user
  deleteUser: async (userId: number): Promise<void> => {
    try {
      await api.delete(`/users/${userId}`);
    } catch (error: any) {
      console.error("Error deleting user:", error);
      throw error;
    }
  },

  // Assign roles
  assignRole: async (userId: number, roleId: number): Promise<void> => {
    try {
      await api.post(`/users/${userId}/roles`, { roleId });
    } catch (error: any) {
      console.error("Error assigning role:", error);
      throw error;
    }
  },

  // Get available roles for current user
  getAvailableRoles: async (userProfile?: IUser): Promise<any[]> => {
    try {
      let endpoint = "/roles";

      if (userProfile?.role?.name === "ROLE_CLIENT_ADMIN") {
        endpoint = "/enterprise/roles";
      } else if (userProfile?.role?.name === "ROLE_FEDERATION_ADMIN") {
        endpoint = "/fed/roles";
      }

      const response = await api.get(endpoint);
      return response.data;
    } catch (error: any) {
      console.error("Error fetching roles:", error);
      throw error;
    }
  },

  // Get user management statistics
  getUserStats: async (): Promise<IUserManagementStats> => {
    try {
      const response = await api.get("/users/stats");
      return response.data;
    } catch (error: any) {
      console.error("Error fetching user stats:", error);
      // Return default stats if API fails
      return {
        totalUsers: 0,
        activeUsers: 0,
        pendingUsers: 0,
        usersByType: {
          ENTERPRISE: 0,
          FEDERATION: 0,
          TRANSPORTER: 0,
        },
      };
    }
  },

  // Get user activity
  getUserActivity: async (userId: number): Promise<IUserActivity[]> => {
    try {
      const response = await api.get(`/users/${userId}/activity`);
      return response.data;
    } catch (error: any) {
      console.error("Error fetching user activity:", error);
      throw error;
    }
  },

  // Get all base account types
  getAllAccountTypes: (): IAccountType[] => {
    return [
      {
        type: "ENTERPRISE",
        title: "Enterprise",
        description: "Manage multiple federations and transporters",
        permissions: [
          "CAN_CREATE_FEDERATION",
          "CAN_CRUD_TRANSPORTER",
          "CAN_CREATE_USER",
        ],
        registrationEndpoint: "/enterprise/registration/enterprise-admin",
      },
      {
        type: "FEDERATION",
        title: "Federation",
        description: "Manage transporters under your federation",
        permissions: ["CAN_CRUD_TRANSPORTER", "CAN_CREATE_USER"],
        registrationEndpoint: "/fed/registration/fed-admin",
      },
      {
        type: "TRANSPORTER",
        title: "Transporter",
        description: "Manage trucks and drivers",
        permissions: [
          "CAN_CRUD_DRIVER_FOR_SELF_OR_TRANSPORTER",
          "CAN_CRUD_TRUCK_TRAILER_FOR_SELF_OR_TRANSPORTER",
        ],
        registrationEndpoint: "/transporter/registration",
      },
    ];
  },

  // Get available account types for current user
  getAvailableAccountTypes: (userProfile?: IUser): IAccountType[] => {
    const baseAccountTypes = UserManagementService.getAllAccountTypes();

    // Filter based on current user's permissions
    if (userProfile?.role?.name === "ROLE_CLIENT_ADMIN") {
      return baseAccountTypes.filter(
        (type) => type.type === "FEDERATION" || type.type === "TRANSPORTER"
      );
    } else if (userProfile?.role?.name === "ROLE_FEDERATION_ADMIN") {
      return baseAccountTypes.filter((type) => type.type === "TRANSPORTER");
    }

    return [];
  },

  // Self-registration
  selfRegister: async (userData: ICreateUserData): Promise<IUser> => {
    try {
      // For self-registration, we need to get all available account types
      // since there's no user profile to filter by permissions
      const accountTypes = UserManagementService.getAllAccountTypes();
      const accountType = accountTypes.find(
        (type) => type.type === userData.userType
      );

      if (!accountType) {
        throw new Error("Invalid account type for self-registration");
      }

      const response = await api.post(accountType.registrationEndpoint, {
        name: userData.name,
        username: userData.username,
        email: userData.email,
        password: userData.password,
        phoneNumber: userData.phoneNumber,
        identifier: userData.identifier,
      });

      return response.data;
    } catch (error: any) {
      console.error("Error in self-registration:", error);
      throw error;
    }
  },
};

export default UserManagementService;
